import React, {useEffect, useMemo} from 'react';
import {Outlet, useLocation, useNavigate} from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import {useGetEmployeeByCurrentUserQuery} from '../../../api/services/employeeService';
import {useGetUserPermissionsQuery} from '../../../api/services/accountService';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import {ROLES} from '../../../constants/roles';
import {hasAnyVisibleStaffSection} from './staffProfileSectionAccess';

const allTabs = [
    {label: 'Basic', path: ''},
    {label: 'Documents', path: 'documentation'},
];

const MyProfileEmployeeDetails = () => {
    const {data = {}, isLoading} = useGetEmployeeByCurrentUserQuery();
    const {data: employeeStaffPermissions = [], isSuccess: areEmployeePermissionsLoaded} =
        useGetUserPermissionsQuery(ROLES.EMPLOYEE);
    const navigate = useNavigate();
    const {pathname} = useLocation();
    const activePath = pathname.includes('/documentation') ? 'documentation' : '';
    // Wait for Employee claims before showing Basic — empty claims mean "all hidden".
    const showBasicTab = areEmployeePermissionsLoaded &&
        hasAnyVisibleStaffSection(employeeStaffPermissions);
    const tabs = useMemo(
        () => allTabs.filter(tab => tab.path !== '' || showBasicTab),
        [showBasicTab]
    );
    const isResolvingBasicTab = activePath === '' &&
        (!areEmployeePermissionsLoaded || !showBasicTab);

    useEffect(() => {
        if (areEmployeePermissionsLoaded && !showBasicTab && activePath === '') {
            navigate('/my-profile/documentation', {replace: true});
        }
    }, [areEmployeePermissionsLoaded, showBasicTab, activePath, navigate]);

    return (
        <Stack gap={2} marginTop={1} flexGrow={1}>
            <Stack>
                {Object.keys(data).length
                    ? (
                        <Typography variant="h4">
                            My Profile
                        </Typography>
                    )
                    : null}
                <Tabs
                    value={tabs.some(tab => tab.path === activePath) ? activePath : (tabs[0]?.path ?? 'documentation')}
                    onChange={(_, newPath) => navigate(newPath ? `/my-profile/${newPath}` : '/my-profile')}
                >
                    {tabs.map(tab => (
                        <Tab
                            key={tab.path}
                            label={tab.label}
                            value={tab.path}
                        />
                    ))}
                </Tabs>
            </Stack>
            {isResolvingBasicTab
                ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={32}/>
                    </Box>
                )
                : (
                    <Outlet context={{data, isLoading, staffPermissions: employeeStaffPermissions}}/>
                )}
        </Stack>
    );
};

export default MyProfileEmployeeDetails;
